/**
 * supernovaOrbs — the four tokens' artwork, and the CSS orb that stands in for it.
 *
 * The real thing is the collection's own image, which lives with the rest of a
 * token's metadata in config/supernovaMeta and is reached here through
 * `orbImage`. The gradients are the fallback: if an image fails to load, the
 * member still sees a token-coloured orb rather than a broken-image icon.
 *
 * These hexes are NOT theme palette. They are the visual identity of four
 * specific ERC-1155 tokens (ids 7–10), the same way a logo has fixed colors:
 * Celestial Flare is gold, Cosmic Pulse is blue, Galactic Ember is ember, and
 * Stellar Nova is violet in every theme, because that is what the member owns.
 * Everything else in this tab uses arc- tokens (see docs/DESIGN_SYSTEM.md §2.1);
 * do not add app chrome colors here, and do not use these anywhere else.
 *
 * Values transcribed from SUPERNOVA_MOCKUP.html (.o7/.o8/.o9/.o10).
 */

import { supernovaArt } from "../../config/supernovaMeta";

const ORB_ART = {
  7: "radial-gradient(circle at 35% 30%, #F0D89A, #C9A24E 60%, #6B4E15)",
  8: "radial-gradient(circle at 35% 30%, #CFE3FF, #7FA8D9 60%, #23405F)",
  9: "radial-gradient(circle at 35% 30%, #FFC9A3, #D97F55 60%, #5F2F16)",
  10: "radial-gradient(circle at 35% 30%, #E3D1FF, #A182D9 60%, #3C2A5F)",
};

/* Neutral fill for an id we don't have art for — a quiet placeholder rather
   than a wrong token's colors. */
const ORB_FALLBACK = "radial-gradient(circle at 35% 30%, var(--arc-ink-4), var(--arc-paper-2) 70%)";

/**
 * The gold ring + glow worn by BOTH the artwork and the fallback orb, so a
 * token whose image fails to load still sits in the same frame as its
 * neighbours. The gold here is `--arc-accent` — the v4 token, theme-aware —
 * not one of the fixed token hexes above, because the ring is app chrome.
 * The glow scales with the orb so a 14px feed bullet isn't wearing a 42px halo.
 */
export function orbRing(size = 42) {
  return `0 0 0 1px var(--arc-accent), 0 0 ${Math.round(size / 3.5)}px var(--arc-accent-soft)`;
}

/** Inline style for one orb. `size` is px. */
export function orbStyle(tokenId, size = 42) {
  return {
    display: "block", // a <span> would otherwise ignore width/height
    width: size,
    height: size,
    borderRadius: "50%",
    flex: "none",
    background: ORB_ART[tokenId] || ORB_FALLBACK,
    boxShadow: `${orbRing(size)}, inset -4px -6px 10px rgba(0,0,0,.35)`,
  };
}

/** Inline style for the artwork itself — same circle, same ring, no inset. */
export function orbImageStyle(size = 42) {
  return {
    display: "block",
    width: size,
    height: size,
    borderRadius: "50%",
    flex: "none",
    objectFit: "cover", // the source art is square; crop, never squash
    background: "var(--arc-paper-2)", // quiet disc while the image decodes
    boxShadow: orbRing(size),
  };
}

/**
 * The bundled artwork for a token id, or null if we have none for it.
 *
 * The art itself lives in config/supernovaMeta alongside the token's name, lore
 * and source CID — this is the orb-shaped view of it, so a token's picture is
 * declared in exactly one place.
 */
export function orbImage(tokenId) {
  return supernovaArt(tokenId);
}

export function hasOrbArt(tokenId) {
  return Object.prototype.hasOwnProperty.call(ORB_ART, tokenId);
}

export default orbStyle;
